import {
  getCookieByName,
  isMyBiz,
  isMyPartner,
  isPartnerMMT,
} from "@tm/insurance/utils";

import { getSessionStorageItem } from "@tm/utility";
import { getInternalSource } from "../..";

import { LOB_NAME_MYPARTNER, pageNameCategoryMap } from "./constants";
import {
  AddPassportEvents,
  CommonLandingEvents,
  getLandingPageAnalytics,
  IAnalytics,
  IOmniData,
  LandingEvents,
  ListingEvents,
  OrderDetailsEvents,
  OrderSummaryEvents,
  PageLoadErrorEvent,
  PageLoadEvent,
  ThankYouEvents,
  TravDetailsEvents,
} from "./IAnalytics";

const omnitureMap = {
  pageName: "eVar15",
  lob: "eVar24",
  siteSection: "m_ch",
  destination: "eVar31",
  ctaAction: "prop52",
  departureDate: "prop4",
  arrivalDate: "prop5",
  noOftravellers: "eVar8",
  travelPurpose: "eVar12",
  sumInsured: "prop46",
  bookingId: "eVar16",
  bookingStatus: "eVar14",
  errorCode: "eVar22",
  cmp: "m_v81",
  experiment: "eVar120",
  modalLoaded: "prop54",
  authStatus: "eVar39",
  userId: "eVar20",
  internalSource: "eVar114",
};

const getMmtInternalSource = () =>
  getInternalSource({ extraParams: ["cmp"], fallback: "fallback" });

export class MmtAnalytics implements IAnalytics {
  private trackEvent(pageName: string, omniData: IOmniData) {
    if (typeof window !== "undefined") {
      const internalSource = getMmtInternalSource();
      omniData = {
        ...omniData,
        pageName,
        ...(internalSource && {
          [omnitureMap["internalSource"]]: internalSource,
        }),
      };
      if (window.omniscriptLoaded) {
        if (typeof window.s !== "undefined") {
          for (const [key, value] of Object.entries(omniData)) {
            window.s[key] = value;
          }
          window.s?.t?.();
        }
      } else {
        window.omnidata?.push?.(omniData);
      }
    }
  }

  trackInteractionEvent(eventData: any, type?: string) {
    if (typeof window === "undefined") return;
    const internalSource = getMmtInternalSource();
    const variableOverrides: Record<string, any> = {};
    for (const [key, value] of Object.entries(eventData)) {
      variableOverrides[key] = value;
      variableOverrides["linkTrackVars"] = key;
    }
    if (internalSource) {
      variableOverrides[omnitureMap["internalSource"]] = internalSource;
    }
    if (type && variableOverrides["pageName"]) {
      variableOverrides[
        "pageName"
      ] = `${variableOverrides["pageName"]}_${type}`;
    }
    // If s is available, fire immediately
    if (window.s && typeof window.s.tl === "function") {
      window.s.tl(true, "o", "ctaAction", variableOverrides);
    } else {
      window.omniInteractionQueue = window.omniInteractionQueue || [];
      window.omniInteractionQueue.push(variableOverrides);
    }
  }

  trackPageLoadEvent(event: PageLoadEvent, type?: string) {
    let { pageName, lob, siteSection } = pageNameCategoryMap[event?.pageName];
    if (event?.v2) pageName = `${pageName}_v2`;
    if (type) pageName = `${pageName}_${type}`;
    lob = isMyPartner() ? LOB_NAME_MYPARTNER : lob;
    const partnerSuffix = isMyBiz()
      ? "|corporate"
      : isMyPartner()
      ? "|mypartner"
      : "";
    const authCookie = getCookieByName("mmt-auth");

    const userInfo = getSessionStorageItem(
      "userInfo",
      true, // isEssential = true (equivalent to itemType.ES)
      { isPiiData: true, shouldParseJson: true }
    );

    let omniData = {
      [omnitureMap["pageName"]]: `${pageName}${partnerSuffix}`,
      [omnitureMap["lob"]]: lob,
      [omnitureMap["siteSection"]]: siteSection,
      ...(event?.bookingId && {
        [omnitureMap["bookingId"]]: event.bookingId,
      }),
      ...(event?.bookingStatus && {
        [omnitureMap["bookingStatus"]]: event.bookingStatus,
      }),
      ...(event?.experiment && {
        [omnitureMap["experiment"]]: event.experiment,
      }),
      ...(authCookie && {
        [omnitureMap["authStatus"]]: "Login",
      }),
      ...(!authCookie && {
        [omnitureMap["authStatus"]]: "Guest",
      }),
      ...(userInfo &&
        userInfo?.uuid && {
          [omnitureMap["userId"]]: userInfo.uuid,
        }),
    };
    if (event.pageName === "LISTING") {
      omniData = {
        ...omniData,
        ...(event.country && {
          [omnitureMap["destination"]]: event.country,
        }),
        ...(event.startDate && {
          [omnitureMap["departureDate"]]: event.startDate,
        }),
        ...(event.endDate && {
          [omnitureMap["arrivalDate"]]: event.endDate,
        }),
        ...(event.purpose && {
          [omnitureMap["travelPurpose"]]: event.purpose,
        }),
        ...(event.travellers && {
          [omnitureMap["noOftravellers"]]: event.travellers,
        }),
      };
    }
    this.trackEvent(`${pageName}${partnerSuffix}`, {
      ...omniData,
      ...(event?.bookingId && { purchaseID: event.bookingId }),
    });
  }

  trackRawPageLoadEvent(event: any, type?: string) {
    if (event?.eVar15) {
      let pageName = event.eVar15;
      if (type) pageName = `${pageName}_${type}`;
      this.trackEvent(pageName, event);
    }
  }

  trackPageLoadErrorEvent(event: PageLoadErrorEvent, type?: string) {
    let { pageName } = pageNameCategoryMap[event?.pageName];
    const partnerSuffix = isMyBiz()
      ? "|corporate"
      : isMyPartner()
      ? "|mypartner"
      : "";
    if (type) pageName = `${pageName}_${type}`;
    const omniData = {
      [omnitureMap["pageName"]]: `${pageName}${partnerSuffix}`,
      [omnitureMap["ctaAction"]]: event.actionType,
      [omnitureMap["errorCode"]]: event.error,
    };
    this.trackEvent(pageName, omniData);
  }

  trackCommonLandingEvents(
    event: CommonLandingEvents,
    eventType: string = "pageView",
    type?: string
  ) {
    let { pageName, lob } = pageNameCategoryMap["COMMON_LANDING"];
    lob = isMyPartner() ? LOB_NAME_MYPARTNER : lob;
    const params = new URLSearchParams(window.location.search);
    const isRetail = params.get("isRetail") === "true";
    if (isRetail) {
      pageName = pageNameCategoryMap["COMMON_LANDING_RETAIL"].pageName;
    } else {
      pageName = pageNameCategoryMap["COMMON_LANDING"].pageName;
    }
    if (type) pageName = `${pageName}_${type}`;
    const omniData = {
      [omnitureMap["pageName"]]: pageName,
      [omnitureMap["lob"]]: lob,
      [omnitureMap["ctaAction"]]: event.actionType,
    };
    /* istanbul ignore if */
    if (event.actionType === "error_shown") {
      omniData[omnitureMap["errorCode"]] = event.payload;
    }
    if (eventType === "pageView") {
      this.trackEvent(pageName, omniData);
    } else {
      this.trackInteractionEvent(omniData);
    }
  }

  trackLandingEvents(
    event: LandingEvents,
    eventType: string = "pageView",
    type?: string
  ) {
    const partnerSuffix = isMyBiz()
      ? "|corporate"
      : isMyPartner()
      ? "|mypartner"
      : "";
    const landingPageAnalytics = getLandingPageAnalytics();
    let pageName, omniData;
    if (landingPageAnalytics) {
      omniData = {
        ...landingPageAnalytics,
        [omnitureMap["ctaAction"]]: event.actionType,
      };
      pageName = omniData.eVar15;
    } else {
      pageName = pageNameCategoryMap[`LANDING`].pageName;
      let lob = pageNameCategoryMap[`LANDING`].lob;
      lob = isMyPartner() ? LOB_NAME_MYPARTNER : lob;
      omniData = {
        [omnitureMap["pageName"]]: `${pageName}${partnerSuffix}`,
        [omnitureMap["lob"]]: lob,
        [omnitureMap["ctaAction"]]: event.actionType,
      };
    }
    if (type) pageName = `${pageName}_${type}${partnerSuffix}`;
    if (
      event.actionType === "other_countries_clicked" ||
      event.actionType === "popular_country_selected" ||
      event.actionType === "searched_countries" ||
      event.actionType === "selected_countries"
    ) {
      omniData[omnitureMap["destination"]] = event.payload;
    } else if (event.actionType === "travel_date_selected") {
      omniData[omnitureMap["departureDate"]] = event.payload.departure;
      omniData[omnitureMap["arrivalDate"]] = event.payload.arrival;
    } else if (event.actionType === "error_shown") {
      omniData[omnitureMap["errorCode"]] = event.payload;
    }
    if (eventType === "pageView") {
      this.trackEvent(`${pageName}${partnerSuffix}`, omniData);
    } else {
      this.trackInteractionEvent(omniData);
    }
  }

  trackListingEvents(
    event: ListingEvents,
    eventType: string = "pageView",
    type?: string,
    v2?: boolean
  ) {
    let { pageName, lob } = pageNameCategoryMap["LISTING"];
    if (v2) pageName = `${pageName}_v2`;
    lob = isMyPartner() ? LOB_NAME_MYPARTNER : lob;
    const partnerSuffix = isMyBiz()
      ? "|corporate"
      : isMyPartner()
      ? "|mypartner"
      : "";
    if (type) pageName = `${pageName}_${type}`;
    const fullPageName = `${pageName}${partnerSuffix}`;
    const omniData: Record<string, string> = {
      [omnitureMap["pageName"]]: fullPageName,
      [omnitureMap["lob"]]: lob,
      [omnitureMap["ctaAction"]]: event.actionType,
    };
    /* istanbul ignore if */
    if (event.actionType === "error_shown") {
      omniData[omnitureMap["errorCode"]] = event.payload;
    } else if (event.actionType === "sum_insured_selected") {
      omniData[omnitureMap["sumInsured"]] = event.payload;
    }
    if (eventType === "pageView") {
      this.trackEvent(fullPageName, omniData);
    } else {
      this.trackInteractionEvent(omniData);
    }
  }

  trackTravDetailsEvents(
    event: TravDetailsEvents,
    eventType: string = "pageView",
    type?: string
  ) {
    let { pageName, lob } = pageNameCategoryMap["REVIEW"];
    lob = isMyPartner() ? LOB_NAME_MYPARTNER : lob;
    const partnerSuffix = isMyBiz()
      ? "|corporate"
      : isMyPartner()
      ? "|mypartner"
      : "";
    if (type) pageName = `${pageName}_${type}`;
    const omniData = {
      [omnitureMap["pageName"]]: `${pageName}${partnerSuffix}`,
      [omnitureMap["lob"]]: lob,
      [omnitureMap["ctaAction"]]: event.actionType,
    };
    if (event.actionType === "error_shown") {
      omniData[omnitureMap["errorCode"]] = event.payload;
    }
    if (eventType === "pageView") {
      this.trackEvent(`${pageName}${partnerSuffix}`, omniData);
    } else {
      this.trackInteractionEvent(omniData);
    }
  }

  trackThankYouEvents(
    event: ThankYouEvents,
    eventType: string = "pageView",
    type?: string
  ) {
    let { pageName, lob } = pageNameCategoryMap[event.pageName];
    lob = isMyPartner() ? LOB_NAME_MYPARTNER : lob;
    const partnerSuffix = isMyBiz()
      ? "|corporate"
      : isMyPartner()
      ? "|mypartner"
      : "";
    if (type) pageName = `${pageName}_${type}`;
    const omniData = {
      [omnitureMap["pageName"]]: `${pageName}${partnerSuffix}`,
      [omnitureMap["lob"]]: lob,
      [omnitureMap["ctaAction"]]: event.actionType,
    };
    if (eventType === "pageView") {
      this.trackEvent(`${pageName}${partnerSuffix}`, omniData);
    } else {
      this.trackInteractionEvent(omniData);
    }
  }

  trackAddPassportEvents(
    event: AddPassportEvents,
    eventType: string = "pageView"
  ) {
    let { pageName, lob } = pageNameCategoryMap["ADD_PASSPORT"];
    lob = isMyPartner() ? LOB_NAME_MYPARTNER : lob;
    const partnerSuffix = isMyBiz()
      ? "|corporate"
      : isMyPartner()
      ? "|mypartner"
      : "";
    const omniData = {
      [omnitureMap["pageName"]]: `${pageName}${partnerSuffix}`,
      [omnitureMap["lob"]]: lob,
      [omnitureMap["ctaAction"]]: event.actionType,
    };
    if (eventType === "pageView") {
      this.trackEvent(`${pageName}${partnerSuffix}`, omniData);
    } else {
      this.trackInteractionEvent(omniData);
    }
  }

  trackOrderSummaryEvents(
    event: OrderSummaryEvents,
    eventType: string = "pageView"
  ) {
    let { pageName, lob } = pageNameCategoryMap["ORDER_SUMMARY"];
    lob = isMyPartner() ? LOB_NAME_MYPARTNER : lob;
    const partnerSuffix = isMyBiz()
      ? "|corporate"
      : isMyPartner()
      ? "|mypartner"
      : "";
    const omniData = {
      [omnitureMap["pageName"]]: `${pageName}${partnerSuffix}`,
      [omnitureMap["pageName"]]: pageName,
      [omnitureMap["lob"]]: lob,
      [omnitureMap["ctaAction"]]: event.actionType,
    };
    if (eventType === "pageView") {
      this.trackEvent(`${pageName}${partnerSuffix}`, omniData);
    } else {
      this.trackInteractionEvent(omniData);
    }
  }

  trackOrderDetailsEvents(
    event: OrderDetailsEvents,
    eventType: string = "pageView"
  ) {
    let { pageName, lob } = pageNameCategoryMap["ORDER_DETAILS"];
    lob = isMyPartner() ? LOB_NAME_MYPARTNER : lob;
    const partnerSuffix = isMyBiz()
      ? "|corporate"
      : isMyPartner()
      ? "|mypartner"
      : "";
    const omniData = {
      [omnitureMap["pageName"]]: `${pageName}${partnerSuffix}`,
      [omnitureMap["lob"]]: lob,
      [omnitureMap["ctaAction"]]: event.actionType,
    };
    if (eventType === "pageView") {
      this.trackEvent(`${pageName}${partnerSuffix}`, omniData);
    } else {
      this.trackInteractionEvent(omniData);
    }
  }
}

// Helper to flush the interaction queue when window.s is ready
function flushOmniInteractionQueue() {
  if (
    typeof window !== "undefined" &&
    window.omniInteractionQueue &&
    window.s &&
    typeof window.s.tl === "function"
  ) {
    window.omniInteractionQueue.forEach((event: any) => {
      window.s.tl(true, "o", "ctaAction", event);
    });
    window.omniInteractionQueue = [];
  }
}

// Listen for a custom event that should be dispatched when analytics script is loaded
if (isPartnerMMT() && typeof window !== "undefined") {
  window.addEventListener("mmt-omniture:ready", flushOmniInteractionQueue);
}

// NOTE: The analytics script should dispatch this event when ready:
// window.dispatchEvent(new Event("omniture:ready"));
